import { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '@services/firebase';
import { RefreshCw, Trash2, Mail, CheckCircle2, Circle } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

// Consulting leads from the public /work-with-me page (tick 6.1). Admins can
// read/manage consulting_leads directly via the client SDK — firestore.rules
// gates read/update/delete on isAdmin().

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'consulting_leads'));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => {
        const aDate = a.createdAt?.toDate?.() ?? new Date(0);
        const bDate = b.createdAt?.toDate?.() ?? new Date(0);
        return bDate - aDate;
      });
      setLeads(list);
    } catch (err) {
      console.error('Failed to load leads:', err);
      toast.error('Failed to load leads (admin role required).');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const toggleHandled = async (id, current) => {
    try {
      await updateDoc(doc(db, 'consulting_leads', id), { handled: !current });
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, handled: !current } : l)));
      toast.success(current ? 'Marked as new' : 'Marked as handled');
    } catch {
      toast.error('Failed to update lead');
    }
  };

  const deleteLead = async (id, name) => {
    if (!window.confirm(`Delete the lead from "${name}"?`)) return;
    try {
      await deleteDoc(doc(db, 'consulting_leads', id));
      setLeads((prev) => prev.filter((l) => l.id !== id));
      toast.success('Lead deleted');
    } catch {
      toast.error('Failed to delete lead');
    }
  };

  const newCount = leads.filter((l) => !l.handled).length;

  return (
    <div className="p-3 sm:p-6 lg:p-8">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Consulting Leads</h1>
          <p className="text-gray-500 text-sm mt-1">
            {leads.length} total · {newCount} new
          </p>
        </div>
        <button
          onClick={fetchLeads}
          className="flex items-center gap-2 px-3 sm:px-4 py-2 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RefreshCw size={15} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-400">Loading…</div>
      ) : leads.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm flex flex-col items-center justify-center py-20 text-gray-400 gap-2">
          <p>No leads yet.</p>
          <p className="text-xs">Submissions from /work-with-me will appear here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {leads.map((lead) => {
            const created = lead.createdAt?.toDate?.();
            return (
              <div
                key={lead.id}
                className={`bg-white rounded-2xl shadow-sm p-4 sm:p-5 border-l-4 ${
                  lead.handled ? 'border-gray-200' : 'border-emerald-400'
                }`}
              >
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="text-sm font-semibold text-gray-800">{lead.name}</span>
                  <a
                    href={`mailto:${lead.email}`}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                  >
                    <Mail size={12} /> {lead.email}
                  </a>
                  {created && (
                    <span className="text-xs text-gray-400">{format(created, 'MMM d, yyyy h:mm a')}</span>
                  )}
                  <span className="ml-auto px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                    {lead.source?.summary ?? 'unknown source'}
                  </span>
                </div>

                <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed mb-3">
                  {lead.message}
                </p>

                <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => toggleHandled(lead.id, lead.handled)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      lead.handled
                        ? 'text-gray-500 bg-gray-100 hover:bg-gray-200'
                        : 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100'
                    }`}
                  >
                    {lead.handled ? (
                      <>
                        <Circle size={13} /> Mark as new
                      </>
                    ) : (
                      <>
                        <CheckCircle2 size={13} /> Mark handled
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => deleteLead(lead.id, lead.name)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-red-500 bg-red-50 text-xs font-medium hover:bg-red-100 transition-colors ml-auto"
                  >
                    <Trash2 size={13} /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
